import { NPSCheck, getNPSMyOrderJson } from 'aster-foundation/core/astercore/nps/redux/actions';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Container, Row } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { flagsmith } from '@common/Lib/Flagsmith';
import DynamicDashboard from '@common/Component/DynamicDashboard/DynamicDashboard';
import Footer from '@common/Component/Footer/Footer';
import { BreadcrumbControl } from '@common/Component/Forms';
import HeaderBelow from '@common/Component/HeaderBelow/HeaderBelow';
import HereToHelp from '@common/Component/HereToHelp/HereToHelp';
import { errorHandlingEpharmacy, getConfigurationValue, getIsInstoreApp } from '@common/Utility/common-utils';
import { ONE_ASTER_CATEGORY, ONE_ASTER_CATEGORY_HOME, SCREEN_TYPE, NPS_EVENTS, SCREEN_NAME, ORDER_STATUSES, FLAGSMITH_KEYS, HOME } from '@common/Utility/constants';
import { dataLayerSendEpharmacyPageViewEvent, dataLayerSendEvent } from './EpharmacyAnalytics';
import { HomePageStyled } from './HomePageStyled';
import UpcomingPharmacyOrders from '@common/Component/UpcomingPharmacyOrders/UpcomingPharmacyOrders';
import useIsNotSafari from '@common/Utility/hooks';
import CustomModal from '@common/Component/Modal/Modal';
import NpsFeedback from '@common/Component/NpsFeedback/NpsFeedback';
import { PAGE_NAME } from '@common/Constants/analytics';
import { getMyOrderJson } from 'aster-foundation/core/epharmacy/myorders/redux/actions';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

function HomePage(props) {
  const dispatch = useDispatch();
  const generateURL = useNavigationURL();
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);
  const guestLogin = useSelector((state) => state?.astercore?.guestAccessToken?.data);
  const appConfigData = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const web_feature_flaging = flagsmith.getValue(FLAGSMITH_KEYS.WEB_FEATURE_FLAGING, {json: true});

  const [isReload, setIsReload] = useState(true);
  const [showNps, setShowNps] = useState(false);
  const [ordersData, setOrdersData] = useState([]);
  const [npsOrderId, setNpsOrderId] = useState(false);
  const [isNpsEnabled, setIsNpsEnabled] = React.useState(false);

  const router = useRouter();
  const { cmsPharmacyResult } = props;
  const orderReference = router?.query?.ref;
  const ae_common_api_actions = flagsmith.getValue(FLAGSMITH_KEYS?.COMMON_API_ACTION, { json: true });
  const enableXUserVcpId = ae_common_api_actions?.xUserVcpId || false;

  useEffect(() => {
    if (orderReference) {
      dataLayerSendEvent('payment_cancelled_or_failed', {
        purchase_type: 'Epharmacy',
        error: 'Payment could not be processed',
      });
    }
  }, [orderReference]);

  useEffect(() => {
    setIsNpsEnabled(getConfigurationValue(appConfigData, 'npsEnabled') === 'true');
  }, [appConfigData]);

  useEffect(() => {
    if (userLogin || guestLogin) {
      setIsReload(!isReload);
    }
  }, [userLogin, guestLogin]);

  useEffect(() => {
    dataLayerSendEpharmacyPageViewEvent({
      screenName: 'epharmacy homepage',
      pageType: 'homepage',
      referrerPage: props?.referrerPage,
    });
  }, []);

  const isNotSafari = useIsNotSafari();

  const npsCheckApiCall = (OrderId) => {
    const params = { eventName: NPS_EVENTS?.AE_ORDER_DELIVERED_WEB, orderId: OrderId };
    dispatch(
      NPSCheck({
        params,
        onSuccess: (response) => {
          if (!response?.data?.isFeedback) {
            setShowNps(true);
          } else {
            setShowNps(false);
          }
        },
        onFailure: (err) => {
          errorHandlingEpharmacy(err);
        },
      }),
    );
  };

  const npsGetMyOrderJson = () => {
    if (!guestLogin) {
      const apiParams = enableXUserVcpId ? { userId: userLogin?.id } : {};
      dispatch(
        getNPSMyOrderJson({
          params: apiParams,
          onSuccess: (response) => {
            const deliveredOrders = response?.body?.orders?.length && response?.body?.orders?.find((orderObj) => orderObj?.order_status === ORDER_STATUSES.DELIVERED);
            if (deliveredOrders?.order_id) {
              setNpsOrderId(deliveredOrders?.order_id);
              npsCheckApiCall(deliveredOrders?.order_id);
            }
          },
          onFailure: (err) => {
            errorHandlingEpharmacy(err);
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (userLogin) {
      getOrdersData();
    }
  }, [userLogin]);

  const getOrdersData = () => {
    const params = {
      isCard: true,
    };
    if(enableXUserVcpId){
      params.userId = userLogin?.id
    }
    dispatch(
      getMyOrderJson({
        params,
        onSuccess: (response) => {
          setOrdersData(response?.body?.orders);
        },
        onFailure: (error) => {
          errorHandlingEpharmacy(error);
        },
      }),
    );
  };

  useEffect(() => {
    localStorage.removeItem('totalReturnValue');
    localStorage.removeItem('cancelOrderData');
    if (isNpsEnabled) {
      npsGetMyOrderJson();
    }
  }, [isNpsEnabled]);

  const analyticsProps = { 
    page_type: "homepage",
    myaster_category: "epharmacy",
    screen_name: "epharmacy homepage",
    referrer_page: props?.referrerPage
  };

  return (
    <HomePageStyled isNotSafari={isNotSafari}>
      <div className="pageContent pb-0">
        <HeaderBelow screenName={SCREEN_NAME?.AE} pageType={PAGE_NAME?.AE} />
        <Container>
          <Row className="py-3">
            <BreadcrumbControl 
              appVariant="epharmacy" 
              childs={web_feature_flaging?.isBreadcrumbAEDisabled ? [] : [{ text: 'Home', link: generateURL(HOME) }]} 
              main={web_feature_flaging?.isBreadcrumbAEDisabled ? "" : "Online Pharmacy" }
            />
          </Row>
        </Container>
        {userLogin && <UpcomingPharmacyOrders screenName = {SCREEN_NAME?.AE} data={ordersData} />}
        <DynamicDashboard
          dynamicValue={{
            cmsResult: cmsPharmacyResult,
            type: ONE_ASTER_CATEGORY_HOME.PHARMACY,
            screenType: SCREEN_TYPE.PHARMACY,
            referrerPage: props?.referrerPage,
            category: ONE_ASTER_CATEGORY_HOME.PHARMACY,
            page_type: 'homepage',
            screenName: 'epharmacy homepage',
            myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
            analyticsProps: analyticsProps,
          }}
        />
        {isNpsEnabled && showNps && (
          <CustomModal noFooter customClass="npsFeedbackModal" size="md">
            <NpsFeedback EventType={NPS_EVENTS?.AE_ORDER_DELIVERED_WEB} OrderId={npsOrderId} setShowNps={setShowNps} />
          </CustomModal>
        )}
        {!getIsInstoreApp() && <HereToHelp />}
        <Footer screenName={SCREEN_NAME?.AE}  pageType={PAGE_NAME?.AE} />
      </div>
    </HomePageStyled>
  );
}
HomePage.defaultProps = {
  props: 'json',
};

HomePage.propTypes = {
  content: PropTypes.any,
  referrerPage: PropTypes.string,
  cmsPharmacyResult: PropTypes.object,
};

export default HomePage;
